import React, { useState, useEffect, useRef } from 'react';
import { Home, Package, Camera, Menu, Award, AlertTriangle, Wrench, Clock, BarChart3, ClipboardList, BookOpen, Users } from 'lucide-react';

// All available options for customizable slots
const SLOT_OPTIONS = {
  qms: { id: 'qms', icon: AlertTriangle, label: 'Laatu' },
  tasks: { id: 'tasks', icon: ClipboardList, label: 'Tehtävät' },
  training: { id: 'training', icon: Award, label: 'Kortit' },
  repairs: { id: 'repairs', icon: Wrench, label: 'Korjaukset' },
  hourReporting: { id: 'hourReporting', icon: Clock, label: 'Tunnit' },
  reports: { id: 'reports', icon: BarChart3, label: 'Raportit' },
  diary: { id: 'diary', icon: BookOpen, label: 'Päiväkirja' },
  crm: { id: 'crm', icon: Users, label: 'CRM' }
};

// Feature flag requirements for each slot option
const SLOT_FEATURES = {
  qms: 'feature_qms',
  hourReporting: 'feature_hour_tracking',
  diary: 'feature_diary',
  crm: 'feature_crm'
};

// Default slot values
const DEFAULT_SLOTS = {
  slot1: 'qms',
  slot2: 'tasks',
  slot3: 'training'
};

// Get slot values from localStorage or use defaults
// Also handles deduplication - if duplicates exist, reset to defaults
const getSlotConfig = () => {
  const slot1 = localStorage.getItem('bottomNavSlot1') || DEFAULT_SLOTS.slot1;
  let slot2 = localStorage.getItem('bottomNavSlot2') || DEFAULT_SLOTS.slot2;
  let slot3 = localStorage.getItem('bottomNavSlot3') || DEFAULT_SLOTS.slot3;

  // Deduplicate: if slot2 equals slot1, find alternative
  if (slot2 === slot1) {
    const available = Object.keys(SLOT_OPTIONS).filter(id => id !== slot1 && id !== slot3);
    slot2 = available[0] || DEFAULT_SLOTS.slot2;
    localStorage.setItem('bottomNavSlot2', slot2);
  }

  // Deduplicate: if slot3 equals slot1 or slot2, find alternative
  if (slot3 === slot1 || slot3 === slot2) {
    const available = Object.keys(SLOT_OPTIONS).filter(id => id !== slot1 && id !== slot2);
    slot3 = available[0] || DEFAULT_SLOTS.slot3;
    localStorage.setItem('bottomNavSlot3', slot3);
  }

  return { slot1, slot2, slot3 };
};

// Save slot config to localStorage
const saveSlotConfig = (slotId, value) => {
  localStorage.setItem(`bottomNav${slotId.charAt(0).toUpperCase() + slotId.slice(1)}`, value);
};

function BottomNav({ activeTab, onTabChange, onAddClick, tenant }) {
  const [isVisible, setIsVisible] = useState(true);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 800);
  const [slotConfig, setSlotConfig] = useState(getSlotConfig());
  const lastScrollY = useRef(0);

  // Check if a slot's required feature is enabled
  const isSlotEnabled = (slotId) => {
    const requiredFeature = SLOT_FEATURES[slotId];
    // If no feature required, or feature is enabled, slot is enabled
    return !requiredFeature || tenant?.[requiredFeature];
  };

  // Listen for window resize
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 800);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for slot config changes (from other components)
  useEffect(() => {
    const handleStorageChange = () => {
      setSlotConfig(getSlotConfig());
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('bottomNavConfigChanged', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('bottomNavConfigChanged', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDelta = currentScrollY - lastScrollY.current;

      const isSmallHeight = window.innerHeight < 500;
      const isLandscape = window.innerWidth > window.innerHeight;
      const isMobile = window.innerWidth <= 800 || (isSmallHeight && isLandscape);

      if (isMobile) {
        if (scrollDelta > 10 && currentScrollY > 100) {
          setIsVisible(false);
        } else if (scrollDelta < -10) {
          setIsVisible(true);
        }
      } else {
        setIsVisible(true);
      }

      lastScrollY.current = currentScrollY;
    };

    // Custom event handler for components with internal scroll (CRM, etc.)
    const handleCustomScroll = (e) => {
      const { visible } = e.detail;
      setIsVisible(visible);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('bottomNavVisibility', handleCustomScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('bottomNavVisibility', handleCustomScroll);
    };
  }, []);

  // Get slot option - if feature not enabled, fall back to safe default
  const getSlotOption = (slotKey) => {
    const optionId = slotConfig[slotKey];
    // If the selected option requires a disabled feature, try defaults
    if (!isSlotEnabled(optionId)) {
      // Try the default for this slot
      const defaultId = DEFAULT_SLOTS[slotKey];
      if (isSlotEnabled(defaultId)) {
        return SLOT_OPTIONS[defaultId];
      }
      // If default also disabled, use 'tasks' (never requires a feature)
      return SLOT_OPTIONS['tasks'];
    }
    return SLOT_OPTIONS[optionId] || SLOT_OPTIONS['tasks'];
  };

  // Build tabs based on desktop/mobile
  const buildTabs = () => {
    const slot1 = getSlotOption('slot1');

    if (isDesktop) {
      // Desktop: 7 buttons
      const slot2 = getSlotOption('slot2');
      const slot3 = getSlotOption('slot3');

      return [
        { id: 'home', icon: Home, label: 'Koti' },
        { id: 'devices', icon: Package, label: 'Kohteet' },
        { id: slot1.id, icon: slot1.icon, label: slot1.label },
        { id: 'scan', icon: Camera, label: 'Skannaa', isHero: true },
        { id: slot2.id, icon: slot2.icon, label: slot2.label },
        { id: slot3.id, icon: slot3.icon, label: slot3.label },
        { id: 'menu', icon: Menu, label: 'Valikko' }
      ];
    } else {
      // Mobile: 5 buttons
      return [
        { id: 'home', icon: Home, label: 'Koti' },
        { id: 'devices', icon: Package, label: 'Kohteet' },
        { id: 'scan', icon: Camera, label: 'Skannaa', isHero: true },
        { id: slot1.id, icon: slot1.icon, label: slot1.label },
        { id: 'menu', icon: Menu, label: 'Valikko' }
      ];
    }
  };

  const tabs = buildTabs();
  const numTabs = tabs.length;

  return (
    <nav style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      height: '72px',
      background: '#1e293b',
      borderTop: '1px solid #334155',
      display: 'grid',
      gridTemplateColumns: `repeat(${numTabs}, 1fr)`,
      maxWidth: isDesktop ? '1000px' : '800px',
      margin: '0 auto',
      paddingBottom: 'max(env(safe-area-inset-bottom), 8px)',
      zIndex: 1000,
      boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.3)',
      transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
      transition: 'transform 0.3s ease-in-out'
    }}>
      {tabs.map(tab => {
        if (tab.isHero) {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <div key={tab.id} style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              {/* Elevated hero button */}
              <div
                onClick={onAddClick}
                style={{
                  position: 'absolute',
                  top: '-24px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #FFEB3B 0%, #FDD835 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(255, 235, 59, 0.5)',
                  transition: 'all 0.3s',
                  border: 'none'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateX(-50%) translateY(-2px) scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 235, 59, 0.6)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateX(-50%)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(255, 235, 59, 0.5)';
                }}
              >
                <Icon size={32} color="#0f172a" strokeWidth={2.5} />
              </div>

              {/* Label below */}
              <span style={{
                fontSize: '11px',
                fontWeight: isActive ? '600' : '400',
                color: isActive ? '#FFEB3B' : '#94a3b8',
                marginTop: '40px',
                whiteSpace: 'nowrap'
              }}>
                {tab.label}
              </span>
            </div>
          );
        }

        const Icon = tab.icon;
        const isActive = activeTab === tab.id;

        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            style={{
              background: 'none',
              border: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              cursor: 'pointer',
              padding: '8px 4px',
              transition: 'all 0.2s',
              WebkitTapHighlightColor: 'transparent'
            }}
          >
            <Icon
              size={isDesktop ? 24 : 26}
              color={isActive ? '#FFEB3B' : '#94a3b8'}
              strokeWidth={isActive ? 2.5 : 2}
            />
            <span style={{
              fontSize: isDesktop ? '11px' : '12px',
              fontWeight: isActive ? '600' : '400',
              color: isActive ? '#FFEB3B' : '#94a3b8',
              whiteSpace: 'nowrap'
            }}>
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

// Export utilities for other components
export const updateSlotConfig = (slotId, value) => {
  saveSlotConfig(slotId, value);
  window.dispatchEvent(new Event('bottomNavConfigChanged'));
};

export const getActiveSlots = () => {
  const config = getSlotConfig();
  const isDesktop = window.innerWidth > 800;

  if (isDesktop) {
    return [config.slot1, config.slot2, config.slot3];
  } else {
    return [config.slot1];
  }
};

export const getAllActiveSlots = () => {
  const config = getSlotConfig();
  return [config.slot1, config.slot2, config.slot3];
};

export { SLOT_OPTIONS, DEFAULT_SLOTS };
export default BottomNav;
